// Pure helpers for project goals (Nordstern), monthly milestones, progress and
// the monthly mandatory check-in. No React, no data dependency.
import { pad } from './format.js';

// --- Month keys --------------------------------------------------------------
// A "period_month" is always the first day of a month as an ISO date string,
// e.g. "2026-06-01". This is what we store in the DB and compare against.

export function monthKeyOf(d = new Date()) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
}

export function currentMonthKey() {
  return monthKeyOf(new Date());
}

// Shift a "YYYY-MM-01" key by n months (n may be negative).
export function addMonthsKey(key, n) {
  const [y, m] = key.split('-').map(Number);
  const total = (y * 12 + (m - 1)) + n;
  const ny = Math.floor(total / 12);
  const nm = (total % 12 + 12) % 12;
  return `${ny}-${pad(nm + 1)}-01`;
}

// Number of whole months from start month to end month, inclusive of both ends.
// e.g. 2026-06 → 2027-06 = 13 entries.
export function monthList(startDate, endDate) {
  if (!startDate || !endDate) return [];
  let cur = monthKeyOf(new Date(startDate));
  const end = monthKeyOf(new Date(endDate));
  if (cur > end) return [];
  const out = [];
  // guard against runaway loops (max 10 years)
  for (let i = 0; i < 121 && cur <= end; i++) {
    out.push(cur);
    cur = addMonthsKey(cur, 1);
  }
  return out;
}

// --- Metrics (Kriterien) -----------------------------------------------------
// A goal has one or more metrics. Two kinds:
//   cumulative — grow start_value → target_value over the goal timeframe
//                (e.g. 5.000 → 20.000 Follower). Tracked as a running total.
//   recurring  — a fixed quota per week/month (e.g. 1 Podcast/Monat, 3 Posts/
//                Woche). Tracked as a count per month that resets each month.

export const METRIC_TYPE = {
  cumulative: { label: 'Wachstumsziel', hint: 'Von Start- auf Zielwert (z. B. Follower)' },
  recurring:  { label: 'Wiederkehrend', hint: 'Feste Anzahl je Woche/Monat (z. B. Podcast, Posts)' },
};

export const CADENCE = {
  weekly:  { label: 'pro Woche', short: '/Woche' },
  monthly: { label: 'pro Monat', short: '/Monat' },
};

// Distribution of a cumulative metric's monthly targets between start and target.
export const RAMP = {
  linear:      { label: 'Linear',       hint: 'Gleichmäßiges Wachstum' },
  exponential: { label: 'Exponentiell', hint: 'Langsamer Start, später stärker' },
};

// Fraction (0..1) of the total delta that should be reached after a fraction f
// (0..1) of the timeframe. Linear = f; exponential = f² (slow start, steep end).
export function rampFraction(f, ramp) {
  const x = Math.max(0, Math.min(1, f));
  return ramp === 'exponential' ? x * x : x;
}

function daysInMonth(monthKey) {
  const [y, m] = monthKey.split('-').map(Number);
  return new Date(y, m, 0).getDate();
}
export function weeksInMonth(monthKey) {
  return daysInMonth(monthKey) / 7;
}

// Target value a metric should reach in the given month.
export function metricMonthTarget(metric, goal, monthKey) {
  if (!metric) return 0;
  if (metric.type === 'recurring') {
    const q = Number(metric.quota) || 0;
    return metric.cadence === 'weekly' ? Math.round(q * weeksInMonth(monthKey)) : q;
  }
  // cumulative: value expected by the END of each month, distributed across the
  // goal timeframe via the chosen ramp (linear or exponential).
  const months = monthList(goal?.start_date, goal?.target_date);
  const start = Number(metric.start_value) || 0;
  const target = Number(metric.target_value) || 0;
  if (months.length === 0) return target;
  if (monthKey < months[0]) return start;
  if (monthKey >= months[months.length - 1]) return target;
  const idx = months.indexOf(monthKey);
  if (idx === -1) return target;
  const f = (idx + 1) / months.length;
  return Math.round(start + (target - start) * rampFraction(f, metric.ramp));
}

// Value reported for a metric in a specific month (or null if none yet).
export function metricMonthValue(progressRows, monthKey) {
  const row = (progressRows || []).find(r => r.period_month === monthKey);
  return row ? Number(row.value) : null;
}

// Headline "actual" for a metric:
//   cumulative — latest reported value (fallback start_value)
//   recurring  — this month's count (fallback 0)
export function metricActual(metric, progressRows, monthKey = currentMonthKey()) {
  if (metric?.type === 'recurring') {
    return metricMonthValue(progressRows, monthKey) ?? 0;
  }
  const rows = (progressRows || []).slice().sort((a, b) => b.period_month.localeCompare(a.period_month));
  if (rows.length > 0) return Number(rows[0].value);
  return Number(metric?.start_value) || 0;
}

// Progress 0..100 for a metric (overall for cumulative, this-month for recurring).
export function metricPct(metric, goal, progressRows, monthKey = currentMonthKey()) {
  const a = metricActual(metric, progressRows, monthKey);
  if (metric?.type === 'recurring') {
    const t = metricMonthTarget(metric, goal, monthKey);
    return t > 0 ? Math.max(0, Math.min(100, Math.round((a / t) * 100))) : (a > 0 ? 100 : 0);
  }
  const start = Number(metric?.start_value) || 0;
  const target = Number(metric?.target_value) || 0;
  const span = target - start;
  if (span === 0) return a >= target ? 100 : 0;
  return Math.max(0, Math.min(100, Math.round(((a - start) / span) * 100)));
}

export function metricStatus(metric, goal, progressRows, monthKey = currentMonthKey()) {
  const target = metricMonthTarget(metric, goal, monthKey);
  const a = metricActual(metric, progressRows, monthKey);
  if (metric?.type === 'recurring') {
    if (target <= 0) return 'on_track';
    const r = a / target;
    return r >= 1 ? 'on_track' : r >= 0.6 ? 'at_risk' : 'off_track';
  }
  const start = Number(metric?.start_value) || 0;
  const delta = target - start;
  if (delta <= 0) return a >= target ? 'on_track' : 'at_risk';
  const r = (a - start) / delta;
  return r >= 1 ? 'on_track' : r >= 0.8 ? 'at_risk' : 'off_track';
}

const STATUS_RANK = { on_track: 0, at_risk: 1, off_track: 2 };
export function worstStatus(statuses) {
  let worst = 'on_track';
  for (const s of (statuses || [])) if ((STATUS_RANK[s] ?? 0) > STATUS_RANK[worst]) worst = s;
  return worst;
}

// Short cadence label for a recurring metric, e.g. "3× /Woche".
export function fmtCadence(metric) {
  if (!metric || metric.type !== 'recurring') return '';
  const q = Number(metric.quota) || 0;
  return `${q}× ${CADENCE[metric.cadence]?.short || ''}`.trim();
}

export const STATUS_META = {
  on_track: { label: 'Im Plan',     emoji: '🟢', color: 'var(--ok)',   soft: 'var(--ok-soft)' },
  at_risk:  { label: 'Wackelt',     emoji: '🟡', color: 'var(--warn)', soft: 'var(--warn-soft)' },
  off_track:{ label: 'Hinterher',   emoji: '🔴', color: 'var(--crit)', soft: 'var(--crit-soft)' },
};

// --- Check-in streak ---------------------------------------------------------
// Consecutive months (ending with the most recent completed month or the
// current month) for which the user submitted a check-in.
export function checkinStreak(checkins, userId, monthKey = currentMonthKey()) {
  const mine = new Set(
    (checkins || []).filter(c => c.user_id === userId).map(c => c.period_month)
  );
  let streak = 0;
  let cur = monthKey;
  // If the current month isn't checked in yet, start counting from last month
  // (don't penalise mid-month).
  if (!mine.has(cur)) cur = addMonthsKey(cur, -1);
  for (let i = 0; i < 121; i++) {
    if (mine.has(cur)) { streak++; cur = addMonthsKey(cur, -1); }
    else break;
  }
  return streak;
}

// --- Formatting --------------------------------------------------------------
const NUM = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });

export function fmtGoalValue(n, unit) {
  const s = NUM.format(Math.round(Number(n) || 0));
  return unit ? `${s} ${unit}` : s;
}

export function fmtMonth(monthKey) {
  if (!monthKey) return '';
  const [y, m] = monthKey.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('de-DE', { month: 'short', year: 'numeric' });
}

export function fmtMonthLong(monthKey) {
  if (!monthKey) return '';
  const [y, m] = monthKey.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
}

// Day of the current month (1..31) — used to escalate the check-in from soft
// nudge to hard block late in the month.
export function dayOfMonth(d = new Date()) {
  return d.getDate();
}

// From which day of the month the check-in becomes non-dismissable.
export const CHECKIN_HARD_DAY = 25;
